import { useRef } from "react";
import { useTranslation } from "react-i18next";
import { motion, useScroll, useTransform } from "framer-motion";
import { ShieldCheck } from "lucide-react";
import { Reveal, Kicker } from "@/components/Reveal";
import { CTASection } from "@/pages/Home";

export default function About({ lang }) {
  const { t } = useTranslation();
  const chapters = t("aboutPage.chapters", { returnObjects: true });
  const guarantees = t("aboutPage.guarantees", { returnObjects: true });
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [50, -50]);

  return (
    <>
      <section data-testid="about-hero" className="pt-40 pb-24 md:pt-52 md:pb-32 px-6 md:px-12 bg-navy text-offwhite overflow-hidden">
        <div className="max-w-[1400px] mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 items-end">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
            className="lg:col-span-7"
          >
            <Kicker light>{t("aboutPage.kicker")}</Kicker>
            <h1 className="mt-6 font-heading font-black uppercase tracking-tighter text-4xl sm:text-6xl lg:text-7xl leading-[0.95]">
              {t("aboutPage.title")}
            </h1>
          </motion.div>
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
            className="lg:col-span-5 text-offwhite/70 text-base md:text-lg leading-relaxed"
          >
            {t("aboutPage.p1")}
          </motion.p>
        </div>
      </section>

      <section ref={ref} data-testid="about-story" className="py-24 md:py-32 px-6 md:px-12">
        <div className="max-w-[1400px] mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-start">
          <motion.div style={{ y: imgY }} className="lg:col-span-5 lg:-mt-24 relative z-10">
            <div className="overflow-hidden">
              <img src="/assets/owner.jpg" alt="Carlos Vindel, owner of VIALFA" className="w-full aspect-[4/5] object-cover" data-testid="about-owner-photo" />
            </div>
            <div className="mt-4 flex items-center gap-3">
              <span className="w-8 h-px bg-orange" />
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-slateblue">{t("aboutPage.ownerCaption")}</p>
            </div>
          </motion.div>
          <div className="lg:col-span-7">
            <Reveal>
              <p className="text-lg md:text-xl leading-relaxed text-navy/90">{t("aboutPage.p2")}</p>
              <p className="mt-6 text-slateblue leading-relaxed">{t("aboutPage.p3")}</p>
            </Reveal>
            <Reveal delay={0.15} className="mt-20">
              <h2 className="font-heading font-bold tracking-tight text-3xl sm:text-4xl">{t("aboutPage.chaptersTitle")}</h2>
            </Reveal>
            <div className="mt-10">
              {chapters.map((ch, i) => (
                <Reveal key={i} delay={i * 0.08}>
                  <div className="group py-8 border-t border-sandline grid grid-cols-12 gap-6 items-baseline">
                    <span className="col-span-3 sm:col-span-2 font-heading font-black text-4xl sm:text-5xl text-offwhite-dark group-hover:text-orange transition-colors duration-300">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <div className="col-span-9 sm:col-span-10">
                      <h3 className="font-heading font-bold text-xl md:text-2xl tracking-tight">{ch.title}</h3>
                      <p className="mt-2 text-slateblue leading-relaxed">{ch.desc}</p>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section data-testid="guarantees-section" className="py-24 md:py-32 bg-navy text-offwhite px-6 md:px-12">
        <div className="max-w-[1400px] mx-auto">
          <Reveal className="mb-16">
            <Kicker light>{t("aboutPage.guaranteesKicker")}</Kicker>
            <h2 className="mt-4 font-heading font-bold tracking-tight text-3xl sm:text-4xl lg:text-5xl">{t("aboutPage.guaranteesTitle")}</h2>
          </Reveal>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-px bg-offwhite/10 border border-offwhite/10">
            {guarantees.map((g, i) => (
              <Reveal key={i} delay={i * 0.04}>
                <div className="bg-navy p-6 md:p-8 h-full flex flex-col gap-4 hover:bg-navy-800 transition-colors duration-300">
                  <ShieldCheck size={22} className="text-orange" strokeWidth={1.6} />
                  <span className="font-heading font-semibold text-sm md:text-base tracking-tight">{g}</span>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <CTASection lang={lang} />
    </>
  );
}
